import { List } from 'immutable'
import type { ReactElement } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import type { HeaderSettingsTab } from './HeaderSettings'
import { previewThemeSettings, previewUserSettings } from '../../store/actions'
import FooterSettings from './FooterSettings'
import HeaderSettings from './HeaderSettings'
import LanguageSwitch from './LanguageSwitch'
import OldLanguageSwitch from './LanguageSwitch.old'
import StorefrontSettings from './StorefrontSettings'
import ThemeSettings from './ThemeSettings'
import useThemeSettings from '../../utils/hooks/useThemeSettings'

type Props = { showLanguages?: boolean }

export default function BottomBar({ showLanguages }: Readonly<Props>): ReactElement {
  const [active, setActive] = useState('')
  const [activeLayer, activeTab = true] = active.split('#')

  const dispatch = useDispatch()

  const userSettings = useSelector<State, ImmutableMap>((state) =>
    state.getIn(['shop', 'userSettings'])?.delete('googleAnalytics'),
  )
  const isAutoTranslationFeatureAvailable = useSelector<State, boolean>((state) =>
    state.getIn(['shop', 'features'], List()).some((feature) => feature.get('name') === 'AUTO_TRANSLATION'),
  )
  const themeSettings = useThemeSettings()

  const sharedInitialUserSettings = useRef(userSettings)
  const sharedInitialThemeSettings = useRef(themeSettings)

  function updateSharedInitialSettings() {
    sharedInitialUserSettings.current = userSettings
    sharedInitialThemeSettings.current = themeSettings
  }

  const resetPreview = useCallback(() => {
    dispatch(previewUserSettings(sharedInitialUserSettings.current))
    dispatch(previewThemeSettings(sharedInitialThemeSettings.current))
  }, [dispatch])

  function createToggleHandler(name: string) {
    return ({ submit = false } = {}) => {
      if (submit) {
        updateSharedInitialSettings()
      } else {
        resetPreview()
      }
      setActive(name === activeLayer ? '' : name)
    }
  }

  useEffect(() => {
    function updateActive() {
      if (window.location.hash === '#logoUpload') {
        setActive('HeaderSettings#shopLogo')
      } else if (window.location.hash === '#headerMenu') {
        setActive('HeaderSettings#headerMenu')
      } else if (window.location.hash === '#themeColors') {
        setActive('ThemeSettings#colors')
      } else if (window.location.hash === '#socialMedia') {
        setActive('FooterSettings#socialNetworks')
      } else if (window.location.hash === '#googleTagManager') {
        setActive('StorefrontSettings#googleTagManager')
      }
    }

    updateActive()
    window.addEventListener('hashchange', updateActive)

    return () => {
      window.removeEventListener('hashchange', updateActive)
      resetPreview()
    }
  }, [resetPreview])

  return (
    <div className="ep-bottom-wrapper">
      <div className="ep-bottom-bar">
        {showLanguages && !isAutoTranslationFeatureAvailable && (
          <OldLanguageSwitch
            active={activeLayer === 'LanguageSwitch'}
            onToggle={createToggleHandler('LanguageSwitch')}
          />
        )}
        {showLanguages && isAutoTranslationFeatureAvailable && (
          <LanguageSwitch active={activeLayer === 'LanguageSwitch'} onToggle={createToggleHandler('LanguageSwitch')} />
        )}
        <HeaderSettings
          active={activeLayer === 'HeaderSettings' && (activeTab as HeaderSettingsTab)}
          onToggle={createToggleHandler('HeaderSettings')}
        />
        <FooterSettings
          active={activeLayer === 'FooterSettings' && activeTab}
          onToggle={createToggleHandler('FooterSettings')}
        />
        <StorefrontSettings
          active={activeLayer === 'StorefrontSettings' && activeTab}
          onToggle={createToggleHandler('StorefrontSettings')}
        />
        <ThemeSettings
          active={activeLayer === 'ThemeSettings' && activeTab}
          onToggle={createToggleHandler('ThemeSettings')}
        />
      </div>
    </div>
  )
}
